<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useHead } from '@vueuse/head'
import { useI18n } from 'vue-i18n'
import { CheckIcon } from '@heroicons/vue/24/outline/index.js'
import { Fancybox } from '@fancyapps/ui'
import '@fancyapps/ui/dist/fancybox/fancybox.css'

// import '~/css/fancybox.css'
import UiAsset from '~/components/ui/ui-asset.vue'

const { t } = useI18n()

useHead({
  title: computed(() => `${t('modules.nav.rooms')} - ${t('project.name')}`),
  meta: [
    {
      name: 'description',
      content: t('pages.rooms.headline.intro'),
    },
  ],
})

onMounted(() => {
  Fancybox.bind('[data-fancybox]', {
    Thumbs: {
      type: 'modern',
    },
  })
})

const images = import.meta.glob<string>('../../assets/rooms/**/*.jpg', { query: '?url', import: 'default', eager: true })

interface Room {
  dir: string
  preview?: string
  slides: string[]
}

const rooms = Object.keys(images).reduce((acc: Room[], key) => {
  const match = key.match(/\/rooms\/([^/]*)\/([^/]*)\.jpg$/)
  if (!match)
    return acc

  const [_, dir]: [_: any, dir: keyof Room] = match as unknown as any

  const room = acc.find(r => r.dir === dir)

  if (images[key].includes('-min')) {
    if (room)
      room.preview = images[key]
    else
      acc.push({ dir, preview: images[key], slides: [] })

    return acc
  }

  if (room)
    room.slides.push(images[key])
  else
    acc.push({ dir, slides: [images[key]] })

  return acc
}, [])
</script>

<template>
  <section class="py-8 md:py-16">
    <div class="max-w-7xl mx-auto px-4">
      <div class="mb-12 md:mb-16 text-center">
        <span class="font-semibold text-accent-500 tracking-wider uppercase">
          {{ $t('pages.rooms.headline.suptitle') }}
        </span>
        <h1 class="font-heading mt-1 mb-5 text-3xl font-semibold text-gray-900 md:text-4xl">
          {{ $t('pages.rooms.headline.title') }}
        </h1>
        <p class="max-w-2xl mt-5 mx-auto text-gray-500">
          {{ $t('pages.rooms.headline.intro') }}
        </p>
      </div>
      <div class="bg-white -mx-4 py-12 mb-12 md:mb-16 md:py-16 lg:mx-0 border-y lg:border-x lg:rounded-lg">
        <div class="px-4 lg:px-8">
          <div class="flex flex-wrap -mx-4">
            <div class="w-full lg:w-1/3 p-4">
              <span class="font-semibold text-accent-500 tracking-wider uppercase">
                {{ t('pages.rooms.features.suptitle') }}
              </span>
              <h2 class="font-heading mt-1 mb-5 text-xl font-semibold text-gray-900 md:text-2xl">
                {{ t('pages.rooms.features.title') }}
              </h2>
              <p class="max-w-lg mt-4 mb-6 text-lg text-gray-500">
                {{ t('pages.rooms.features.description') }}
              </p>
              <span class="text-sm text-gray-800 font-semibold"
                    v-html="t('pages.rooms.features.notice', ['<br>'])"
              />
            </div>
            <div class="w-full lg:hidden p-4">
              <hr class="my-8">
            </div>
            <div class="w-full lg:w-2/3 p-4">
              <div class="lg:pl-12">
                <div class="flex -m-2 flex-wrap">
                  <div v-for="idx in 9"
                       :key="idx"
                       class="w-full p-2 xs:w-1/2"
                  >
                    <div class="relative">
                      <CheckIcon class="absolute h-6 w-6 text-accent-500" aria-hidden="true" />
                      <p class="ml-9 font-medium text-gray-900">
                        {{ $t(`pages.rooms.features.listing.${idx - 1}`) }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="space-y-6 md:space-y-12">
        <div v-for="(room, index) in rooms"
             :key="index"
             class="flex flex-wrap -mx-4 pb-6 md:pb-12 items-center md:even:flex-row-reverse border-b border-gray-300 last:border-0 group"
        >
          <div class="w-full md:w-1/2 p-4">
            <div class="md:max-w-lg md:group-even:ml-auto">
              <span class="font-semibold text-accent-500 tracking-wider uppercase">
                {{ t(`pages.rooms.listing[${index}].suptitle`) }}
              </span>
              <h2 class="font-heading mt-1 text-lg font-semibold text-gray-900 md:text-xl">
                {{ t(`pages.rooms.listing[${index}].name`) }}
              </h2>
              <p class="mt-5 text-gray-500 md:text-justify">
                {{ t(`pages.rooms.listing[${index}].description`) }}
              </p>
            </div>
          </div>
          <div class="w-full md:w-1/2 p-4">
            <div class="relative aspect-[5/3] md:aspect-square lg:aspect-[5/3]">
              <UiAsset :src="room.preview"
                       :alt="t(`pages.rooms.listing[${index}].suptitle`)"
                       class="object-center object-cover rounded-lg w-full h-full cursor-pointer"
              />
              <img v-for="(image, idx) in room.slides"
                   :key="idx"
                   :data-fancybox="index"
                   :data-src="image"
                   :alt="t(`pages.rooms.listing[${index}].suptitle`)"
                   :data-caption="`${t(`pages.rooms.listing[${index}].name`)} - ${t(`pages.rooms.listing[${index}].suptitle`)}`"
                   class="hidden"
              >
              <div v-if="room.slides.length > 1"
                   class="absolute inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center transition-all cursor-pointer hover:bg-opacity-30"
                   :data-fancybox-trigger="index"
              >
                <span class="text-white uppercase font-semibold select-none">
                  {{ t('components.gallery.view-photos') }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style>
.fancybox__thumbs .carousel__slide .fancybox__thumb::after {
  border-color: theme('colors.accent.500');
}

.fancybox__progress {
  background: theme('colors.accent.500');
}
</style>
